import * as React from 'react';
import { createRootRouteWithContext, Outlet, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

import { SESSION_QUERY_KEY, type SessionInfo } from '@/lib/auth';
import { api } from '@/lib/api';

interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Root route — sets up shell + global error boundary.
 *
 * Authentication routing:
 *   - `/login`            → public, no shell.
 *   - `/operator/login`   → public, no shell.
 *   - everything else     → requires session; shell wraps Outlet.
 *
 * The session probe runs as a beforeLoad guard. Public routes opt out by
 * declaring `staticData: { public: true }` on their route definition.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location, matches }) => {
    const isPublic = matches.some(
      (m) => (m.staticData as { public?: boolean } | undefined)?.public === true
    );

    const session = await context.queryClient.fetchQuery({
      queryKey: SESSION_QUERY_KEY,
      queryFn: async () => {
        const res = await api.api.auth.session.$get();
        if (!res.ok) return null;
        const json = (await res.json()) as { session: SessionInfo | null };
        return json.session;
      },
      staleTime: 60_000,
    });

    if (!session && !isPublic) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
    if (session && (location.pathname === '/login' || location.pathname === '/operator/login')) {
      throw redirect({ to: session.role === 'operator' ? '/operator' : '/' });
    }

    return { session };
  },
  component: RootComponent,
});

function RootComponent() {
  return (
    <div className="min-h-screen bg-bg text-text antialiased">
      <Outlet />
    </div>
  );
}
